import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import env from '../config/env';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const { token } = useAuth(); // Monitor token changes për të refreshuar settings pas login

  useEffect(() => {
    const fetchSettings = async () => {
      // Nëse nuk ka token, mos bëj request për settings (përdoruesi nuk është i kyçur)
      if (!token) {
        setSettings({
          emri_duqanit: '',
          monedha: 'Denar',
        });
        setLoading(false);
        return;
      }

      // Nëse jemi në setup mode (nuk ka tabela), mos bëj request për settings
      try {
        const checkSetup = await apiService.get('/check-setup');
        if (checkSetup.is_setup_needed) {
          // Në setup mode, përdor defaults
          setSettings({
            emri_duqanit: '',
            monedha: 'Denar',
          });
          setLoading(false);
          return;
        }
      } catch (error) {
        // Nëse ka error, supozojmë që jemi në setup mode
        setSettings({
          emri_duqanit: '',
          monedha: 'Denar',
        });
        setLoading(false);
        return;
      }

      try {
        const data = await apiService.get('/settings');
        // Log për debug
        console.log('Settings fetched:', {
          furnizuesit_aktiv: data?.furnizuesit_aktiv,
          type: typeof data?.furnizuesit_aktiv,
          fullData: data
        });
        setSettings(data);
        
        // Set logo URL - përdor logo_url nëse ekziston, përndryshe ndërto nga logo_path
        if (data?.logo_url) {
          // Nëse logo_url tashmë është URL e plotë, përdor direkt
          if (data.logo_url.startsWith('http://') || data.logo_url.startsWith('https://')) {
            setLogoUrl(data.logo_url);
          } else {
            // Nëse është relative path, shto base URL
            const baseUrl = env.API_URL_Image || env.API_URL || '';
            // Hiq /api nga fund nëse ekziston
            const cleanBaseUrl = baseUrl.replace(/\/api$/, '');
            setLogoUrl(`${cleanBaseUrl}${data.logo_url.startsWith('/') ? '' : '/'}${data.logo_url}`);
          }
        } else if (data?.logo_path) {
          // Nëse logo_path tashmë ka http/https, përdor direkt
          if (data.logo_path.startsWith('http://') || data.logo_path.startsWith('https://')) {
            setLogoUrl(data.logo_path);
          } else {
            // Përndryshe, ndërto URL-në e plotë
            // Përdor API_URL_Image ose API_URL dhe shto /storage/
            const baseUrl = env.API_URL_Image || env.API_URL || '';
            // Hiq /api nga fund nëse ekziston
            let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
            // Nëse baseUrl nuk përfundon me /public, shto /public
            if (!cleanBaseUrl.endsWith('/public')) {
              cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
            }
            setLogoUrl(`${cleanBaseUrl}/storage/${data.logo_path}`);
          }
        } else {
          setLogoUrl(null);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Set defaults
        setSettings({
          emri_duqanit: '',
          monedha: 'Denar',
        });
      } finally {
        setLoading(false);
      }
    };

    // Nëse ka token, fetch settings (pas login)
    // Nëse nuk ka token, reset settings (pas logout)
    if (token) {
      fetchSettings();
    } else {
      // Pas logout, reset settings
      setSettings(null);
      setLogoUrl(null);
      setLoading(false);
    }
  }, [token]); // Refresh settings kur token ndryshon (login/logout)

  // Funksion për të refreshuar settings manualisht
  const refreshSettings = React.useCallback(async () => {
    // Nëse nuk ka token, mos bëj request
    if (!token) {
      return;
    }

    try {
      const data = await apiService.get('/settings');
      
      // Log për debug
      console.log('Settings refreshed:', {
        furnizuesit_aktiv: data?.furnizuesit_aktiv,
        type: typeof data?.furnizuesit_aktiv
      });
      
      setSettings(data);
      
      // Update logo URL
      if (data?.logo_url) {
        if (data.logo_url.startsWith('http://') || data.logo_url.startsWith('https://')) {
          setLogoUrl(data.logo_url);
        } else {
          const baseUrl = env.API_URL_Image || env.API_URL || '';
          const cleanBaseUrl = baseUrl.replace(/\/api$/, '');
          setLogoUrl(`${cleanBaseUrl}${data.logo_url.startsWith('/') ? '' : '/'}${data.logo_url}`);
        }
      } else if (data?.logo_path) {
        if (data.logo_path.startsWith('http://') || data.logo_path.startsWith('https://')) {
          setLogoUrl(data.logo_path);
        } else {
          const baseUrl = env.API_URL_Image || env.API_URL || '';
          let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
          if (!cleanBaseUrl.endsWith('/public')) {
            cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
          }
          setLogoUrl(`${cleanBaseUrl}/storage/${data.logo_path}`);
        }
      } else {
        setLogoUrl(null);
      }
    } catch (error) {
      console.error('Error refreshing settings:', error);
    }
  }, [token]);

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    
    // Update logo URL - përdor logo_url nëse ekziston, përndryshe ndërto nga logo_path
    if (newSettings?.logo_url) {
      // Nëse logo_url tashmë është URL e plotë, përdor direkt
      if (newSettings.logo_url.startsWith('http://') || newSettings.logo_url.startsWith('https://')) {
        setLogoUrl(newSettings.logo_url);
      } else {
        // Nëse është relative path, shto base URL
        const baseUrl = env.API_URL_Image || env.API_URL || '';
        const cleanBaseUrl = baseUrl.replace(/\/api$/, '');
        setLogoUrl(`${cleanBaseUrl}${newSettings.logo_url.startsWith('/') ? '' : '/'}${newSettings.logo_url}`);
      }
    } else if (newSettings?.logo_path) {
      // Nëse logo_path tashmë ka http/https, përdor direkt
      if (newSettings.logo_path.startsWith('http://') || newSettings.logo_path.startsWith('https://')) {
        setLogoUrl(newSettings.logo_path);
      } else {
        // Përndryshe, ndërto URL-në e plotë
        const baseUrl = env.API_URL_Image || env.API_URL || '';
        let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
        // Nëse baseUrl nuk përfundon me /public, shto /public
        if (!cleanBaseUrl.endsWith('/public')) {
          cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
        }
        setLogoUrl(`${cleanBaseUrl}/storage/${newSettings.logo_path}`);
      }
    } else {
      setLogoUrl(null);
    }
  };

  const getShopName = React.useCallback(() => {
    return settings?.emri_duqanit || '';
  }, [settings?.emri_duqanit]);

  const getLogoUrl = React.useCallback(() => {
    return logoUrl || '/logo.png';
  }, [logoUrl]);

  const getCurrency = React.useCallback(() => {
    return settings?.monedha || 'Denar';
  }, [settings?.monedha]);

  const getCurrencyCode = React.useCallback(() => {
    return settings?.monedha_kodi || 'DEN';
  }, [settings?.monedha_kodi]);

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      logoUrl,
      updateSettings,
      refreshSettings,
      getShopName,
      getLogoUrl,
      getCurrency,
      getCurrencyCode,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

